/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link } from 'react-router';

import {
  TranslateContentButton,
  TranslatedPlainText,
  TranslationContextProvider,
  TranslationFailedNotification,
} from '@coursera-web/ondemand-translation';
import classNames from 'classnames';
import moment from 'moment';
import { string } from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Grid, Typography, Typography2, withTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { PinIcon } from '@coursera/cds-icons';

import LikeButton from 'bundles/discussions/components/forumsV2-LikeButtonWithData';
import ReplyButton from 'bundles/discussions/components/forumsV2-ReplyButton';
import ThreadBadge from 'bundles/discussions/components/personalization-ForumThreadCard/__components/ThreadBadge';
import { isContributorProfile } from 'bundles/discussions/lib/guards';
import type { AuthorProfile, Contributor } from 'bundles/discussions/lib/types';
import { initialsFromName } from 'bundles/discussions/utils/forumsV2-nameParser';

import _t from 'i18n!nls/discussions';

import ProfileAvatar from '../forumsV2-ProfileAvatar';
import { ShimmerParagraph, ShimmerSentence, shimmerColor } from '../forumsV2-ShimmerLib';

type Props = {
  title?: string;
  titleLink?: JSX.Element;
  threadUrl?: string;
  creator?: AuthorProfile;
  tags?: JSX.Element;
  description: JSX.Element | string;
  isUpvoted: boolean;
  upvoteCount: number;
  replyCount?: number;
  createdAt?: number | string;
  metadataLabel?: string;
  forumQuestionId: string;
  courseId?: string;
  deepLink?: string;
  lastReplyDate?: number | string;
  lastContributor?: Contributor;
  answerCount?: number;
  badge?: string;
  theme: Theme;
  pinned?: boolean;
  forumName?: string;
  myPostOrReplyBadgeText?: string;
  isRead?: boolean;
  isClosed?: boolean;
};

export function PostAge({ createdAt, answerCount }: { createdAt: number | string; answerCount?: number }) {
  const prefix = answerCount && answerCount > 0 ? 'replied' : 'posted';
  return <span>{prefix + ' ' + moment(new Date(createdAt)).fromNow()}</span>;
}

export function ContributorNameWithIcon({
  fullName,
  photoUrl,
  className,
}: {
  fullName: string;
  photoUrl?: string;
  className?: string;
}) {
  const initials = (fullName && initialsFromName(fullName)) || '--';
  return (
    <div className={className}>
      <ProfileAvatar color="var(--cds-color-blue-50)" initials={initials} photoUrl={photoUrl} name={fullName} />
      <span
        css={{
          lineHeight: '36px',
          display: 'inline-block',
          paddingLeft: '7px',
        }}
      >
        {fullName}
      </span>
    </div>
  );
}

class _ForumThreadCard extends React.LegacyComponentWithChildren<Props> {
  static contextTypes = {
    courseId: string,
    userId: string,
  };

  declare context: LegacyContextType<typeof _ForumThreadCard.contextTypes>;

  render() {
    const {
      title,
      titleLink,
      description,
      creator,
      badge,
      lastReplyDate,
      lastContributor,
      forumName,
      myPostOrReplyBadgeText,
      forumQuestionId,
      isRead,
      isClosed,
      replyCount,
    } = this.props;

    const userIdCourseIdQuestionIdCombo = forumQuestionId?.includes('~')
      ? forumQuestionId
      : `${this.context.userId}~${this.context.courseId}~${forumQuestionId}`;

    return (
      <TranslationContextProvider>
        <Grid container className="rc-personalization_ForumThreadCard">
          {forumName && (
            <Grid>
              <Grid item>
                <Typography2
                  component="p"
                  variant="bodySecondary"
                  color="supportText"
                  css={{
                    margin: this.props.theme.spacing(0, 0, 8, 0),
                  }}
                >
                  {forumName}
                </Typography2>
              </Grid>
            </Grid>
          )}
          <Grid container item css={{ justifyContent: 'flex-start', flexFlow: 'initial' }}>
            {this.props.pinned && (
              <Grid item>
                <PinIcon
                  aria-label={_t('pinned forum post')}
                  size="medium"
                  css={{
                    margin: '0 var(--cds-spacing-100) 0 0',
                    background: 'var(--cds-color-yellow-200)',
                    padding: '3px',
                    height: '24px',
                    width: '24px',
                    borderRadius: '100%',
                  }}
                />
              </Grid>
            )}
            <Grid item>
              {!titleLink && title && (
                <Typography
                  variant={isRead ? 'h2' : 'h2semibold'}
                  className="rc-personalization_ForumThreadCard__title"
                >
                  <TranslatedPlainText enableTranslation plainText={title} />
                </Typography>
              )}
              {titleLink}
            </Grid>
          </Grid>
          <Grid item xs={12} css={{ padding: 'var(--cds-spacing-150) 0 var(--cds-spacing-200) 0' }}>
            {description}
          </Grid>
          {(badge || myPostOrReplyBadgeText) && (
            <Grid
              item
              xs={12}
              className="rc-personalization_ForumThreadCard__content__tags"
              css={{ padding: this.props.theme.spacing(0, 0, 24, 0) }}
            >
              {badge && <ThreadBadge badge={badge} />}
              {myPostOrReplyBadgeText && <ThreadBadge badge={myPostOrReplyBadgeText} />}
              {isClosed && (
                <ThreadBadge
                  badge="CLOSED"
                  css={
                    badge
                      ? css`
                          margin-left: var(--cds-spacing-100);
                        `
                      : undefined
                  }
                />
              )}
            </Grid>
          )}
          <Grid item>
            <TranslationFailedNotification />
          </Grid>

          <Grid container item css={{ justifyContent: 'space-between' }}>
            <Grid item className="rc-personalization_ForumThreadCard__content__responseControls">
              {replyCount !== undefined && creator && (
                <span className="rc-personalization_ForumThreadCard__content__responseControls__controlContainer">
                  {isContributorProfile(creator) && (
                    <span
                      aria-label={_t('Like #{title}', {
                        title,
                      })}
                      className="rc-personalization_ForumThreadCard__content__responseControls__controlContainer__button"
                    >
                      <LikeButton
                        creator={creator as Contributor}
                        forumPostId={userIdCourseIdQuestionIdCombo}
                        upvotes={this.props.upvoteCount}
                        isUpvoted={this.props.isUpvoted}
                        title={title}
                      />
                    </span>
                  )}
                  {this.props.threadUrl && !isClosed && (
                    <div
                      className="rc-personalization_ForumThreadCard__content__responseControls__controlContainer__button"
                      css={{
                        display: 'inline',
                      }}
                    >
                      <ReplyButton
                        to={this.props.threadUrl}
                        forumQuestionId={forumQuestionId}
                        replies={replyCount}
                        creator={creator}
                        ariaLabel={_t('Reply #{count} to post #{title}', {
                          title,
                          count: replyCount || '',
                        })}
                        componentType={Link}
                        role="link"
                      />
                    </div>
                  )}
                </span>
              )}
              <TranslateContentButton courseId={this.context.courseId} eventingSectionName="discussion_forum" />
            </Grid>
            <Grid item>
              <Typography2
                variant="bodySecondary"
                component="span"
                css={{
                  display: 'flex',
                  flexFlow: 'row',
                  alignItems: 'center',
                  lineHeight: '36px',
                }}
              >
                {lastContributor ? (
                  <ContributorNameWithIcon
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    fullName={lastContributor.fullName}
                    photoUrl={lastContributor.photoUrl}
                  />
                ) : (
                  <span
                    css={{
                      lineHeight: '36px',
                      display: 'inline-block',
                      paddingLeft: '7px',
                    }}
                  >
                    {_t('Inactive Learner')}
                  </span>
                )}
                <span css={{ padding: '0 var(--cds-spacing-50)' }}>·</span>
                {this.props.tags && (
                  <span
                    css={{
                      lineHeight: '36px',
                    }}
                  >
                    {this.props.tags}
                  </span>
                )}
                <span
                  css={{
                    lineHeight: '36px',
                  }}
                >
                  <PostAge
                    createdAt={lastReplyDate || this.props.createdAt || 0}
                    answerCount={this.props.answerCount || 0}
                  />
                </span>
              </Typography2>
            </Grid>
          </Grid>
          {this.props.children && <Grid item>{this.props.children}</Grid>}
        </Grid>
      </TranslationContextProvider>
    );
  }
}

export function CardShimmer() {
  return (
    <div className="rc-personalization_ForumThreadCard">
      <div className="rc-personalization_ForumThreadCard__content">
        <ShimmerSentence width="100px" />
        <span
          className={classNames('comment', 'br', 'animated', 'rc-personalization_ForumThreadCard__content__PostAge')}
          style={{ backgroundColor: shimmerColor, display: 'block', height: '10px', width: '40px' }}
        />
      </div>

      <ShimmerParagraph />
    </div>
  );
}

export const ForumThreadCard = withTheme(_ForumThreadCard);
